import moment from 'moment-timezone';
import { useEffect, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { setAIAssistantDisplayed } from '@/aiAssistant/aiAssistant.actions';
import { getDefaultDisplayed } from '@/aiAssistant/aiAssistant.utilities';
import { subscribeToAuthChangeChannel } from '@/homescreen/homescreen.actions';
import { LicenseInitialization } from '@/licenseManagement/LicenseInitialization';
import { LoadError } from '@/main/LoadError.page';
import { LoadingFallback } from '@/main/LoadingFallback.atom';
import { StoreProviders } from '@/main/StoreProviders';
import { cleanupOnUnload } from '@/main/cleanup.utilities';
import { isEmbeddedView, setNavigate } from '@/main/routing.utilities';
import { resolveTelemetryConfig } from '@/main/telemetry.utilities';
import { cancelRequestsOnUnload } from '@/requests/pendingRequests.utilities';
import { cleanupBroadcastChannels } from '@/services/broadcastChannel.utilities';
import { addEventListeners } from '@/services/notifier.service';
import { fetchConfiguration } from '@/services/systemConfiguration.actions';
import { SystemMessageListener } from '@/systemWarning/SystemMessageListener';
import { Tracker } from '@/track/Tracker.atom';
import { isCsrfSet } from '@/utilities/auth.utilities';
import { reload } from '@/utilities/browserLocation.utilities';
import { init18n } from '@/utilities/i18n.utilities';
import { logError } from '@/utilities/logger';
import { formatMessage } from '@/utilities/logger.utilities';
import { TelemetryProvider, useRouteTelemetry } from '@seeqdev/telemetry';

/**
 * Wrapper component that wraps all the Route components.
 * Everything that has to happen once per application load and applies to every single component should happen here.
 */
export const Main = () => {
  const navigate = useNavigate();
  const [bootstrapStatus, setBootstrapStatus] = useState<'loading' | 'loaded' | 'failed'>('loading');

  // Publish the current route onto telemetry metrics. No-op until telemetry is
  // initialized below (only when enabled), and safe to call before the loading
  // gate since hooks must run unconditionally.
  useRouteTelemetry();

  useEffect(() => {
    // Defaults moment to the browser's locale. init18n() replaces it with the locale data for the browser's language.
    moment.locale(window.navigator.language);
    // i18n does not read the configuration, so both load in parallel. Waiting for both to settle, even when one has
    // already failed, lets the error page render translated.
    void Promise.allSettled([fetchConfiguration(), init18n()]).then((results) => {
      const failure = results.find((result) => result.status === 'rejected');
      if (failure) {
        logError(formatMessage`Failed to load the application: ${failure.reason}`);
        setBootstrapStatus('failed');
        return;
      }

      if (!isEmbeddedView()) {
        setAIAssistantDisplayed(getDefaultDisplayed(), true);
      }
      setBootstrapStatus('loaded');
    });
    subscribeToAuthChangeChannel();
    addEventListeners();

    // we need to expose the navigate function to call it from services
    setNavigate(navigate);
    const beforeUnloadListener = () => cancelRequestsOnUnload();
    window.addEventListener('beforeunload', beforeUnloadListener);
    return () => {
      cleanupBroadcastChannels();
      cancelRequestsOnUnload();
      cleanupOnUnload();
      window.removeEventListener('beforeunload', beforeUnloadListener);
    };
  }, []);

  // Rendered in place rather than by navigating to /connection-error, because that route is a child of this component
  // and would never render, and because reloading in place keeps the URL the user asked for.
  if (bootstrapStatus === 'failed') {
    return (
      <LoadError
        header="LOAD_ERROR.SERVER_HEADER"
        message1="LOAD_ERROR.SERVER_MESSAGE1"
        message2="LOAD_ERROR.SERVER_MESSAGE2"
        retryInterval={3000}
        retry={reload}
      />
    );
  }

  if (bootstrapStatus === 'loading') {
    return <LoadingFallback />;
  }

  // Resolve telemetry config now that configuration has loaded; null when the
  // operator has not enabled telemetry.
  const telemetryConfig = resolveTelemetryConfig();

  return (
    <LicenseInitialization enabled={isCsrfSet()}>
      <StoreProviders>
        <SystemMessageListener />
        <Tracker />
        {telemetryConfig ? (
          <TelemetryProvider config={telemetryConfig}>
            <Outlet />
          </TelemetryProvider>
        ) : (
          <Outlet />
        )}
      </StoreProviders>
    </LicenseInitialization>
  );
};
